import React, { useState, useEffect, useCallback } from 'react';
import { inventoryApi, supplierApi, purchaseOrderApi, projectApi } from '../services/api';
import { Button, Input, Select, Card, Modal, Badge } from '../components/UI';
import {
  Warehouse, Plus, Trash, Package, Truck, ShoppingCart, ArrowDown, ArrowUp,
  PencilSimple, CheckCircle, CaretDown, CaretRight, Receipt
} from '@phosphor-icons/react';

const fmt = (v) => new Intl.NumberFormat('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

export default function InventoryPage() {
  const [tab, setTab] = useState('stock');
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stock, setStock] = useState([]);
  const [movements, setMovements] = useState([]);
  const [pos, setPOs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selWarehouse, setSelWarehouse] = useState('');
  const [selProject, setSelProject] = useState('');
  const [showWhModal, setShowWhModal] = useState(false);
  const [showSupModal, setShowSupModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showPOModal, setShowPOModal] = useState(false);
  const [error, setError] = useState('');

  const [whForm, setWhForm] = useState({ name: '', location: '', project_id: '' });
  const [supForm, setSupForm] = useState({ name: '', contact_person: '', email: '', phone: '', address: '', trn: '', payment_terms: 'net_30' });
  const [moveForm, setMoveForm] = useState({ warehouse_id: '', material_name: '', movement_type: 'in', quantity: '', unit_cost: '', project_id: '', activity_id: '', reference_type: 'manual', notes: '' });
  const [poForm, setPOForm] = useState({ supplier_id: '', warehouse_id: '', project_id: '', items: [{ material_name: '', quantity: '', unit: 'NOS', unit_cost: '', tax_rate: 5 }], currency: 'AED', notes: '' });

  const load = useCallback(async () => {
    try {
      const [whRes, supRes, projRes, poRes] = await Promise.all([
        inventoryApi.listWarehouses(), supplierApi.list(), projectApi.list(), purchaseOrderApi.list()
      ]);
      setWarehouses(whRes.data);
      setSuppliers(supRes.data);
      setProjects(projRes.data);
      setPOs(poRes.data);
    } catch (e) { setError('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadStock = useCallback(async () => {
    const params = {};
    if (selWarehouse) params.warehouse_id = selWarehouse;
    if (selProject) params.project_id = selProject;
    const [stockRes, moveRes] = await Promise.all([
      inventoryApi.getStock(params), inventoryApi.listMovements(params)
    ]);
    setStock(stockRes.data);
    setMovements(moveRes.data);
  }, [selWarehouse, selProject]);

  useEffect(() => { loadStock(); }, [loadStock]);

  const handleCreateWh = async (e) => {
    e.preventDefault(); setError('');
    try { await inventoryApi.createWarehouse(whForm); setShowWhModal(false); setWhForm({ name: '', location: '', project_id: '' }); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const handleCreateSup = async (e) => {
    e.preventDefault(); setError('');
    try { await supplierApi.create(supForm); setShowSupModal(false); setSupForm({ name: '', contact_person: '', email: '', phone: '', address: '', trn: '', payment_terms: 'net_30' }); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const handleMove = async (e) => {
    e.preventDefault(); setError('');
    try {
      const data = { ...moveForm, quantity: parseFloat(moveForm.quantity) || 0, unit_cost: parseFloat(moveForm.unit_cost) || 0 };
      await inventoryApi.createMovement(data);
      setShowMoveModal(false); setMoveForm({ warehouse_id: '', material_name: '', movement_type: 'in', quantity: '', unit_cost: '', project_id: '', activity_id: '', reference_type: 'manual', notes: '' });
      loadStock();
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const addPOItem = () => setPOForm({ ...poForm, items: [...poForm.items, { material_name: '', quantity: '', unit: 'NOS', unit_cost: '', tax_rate: 5 }] });
  const updatePOItem = (idx, field, val) => { const items = [...poForm.items]; items[idx][field] = val; setPOForm({ ...poForm, items }); };
  const removePOItem = (idx) => { const items = poForm.items.filter((_, i) => i !== idx); setPOForm({ ...poForm, items }); };

  const handleCreatePO = async (e) => {
    e.preventDefault(); setError('');
    try {
      const data = { ...poForm, items: poForm.items.map(i => ({ ...i, quantity: parseFloat(i.quantity) || 0, unit_cost: parseFloat(i.unit_cost) || 0, tax_rate: parseFloat(i.tax_rate) || 5 })) };
      await purchaseOrderApi.create(data);
      setShowPOModal(false); setPOForm({ supplier_id: '', warehouse_id: '', project_id: '', items: [{ material_name: '', quantity: '', unit: 'NOS', unit_cost: '', tax_rate: 5 }], currency: 'AED', notes: '' });
      load();
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const handleApprovePO = async (id) => { try { await purchaseOrderApi.approve(id); load(); } catch (err) { setError(err.response?.data?.detail || 'Failed'); } };
  const handleReceivePO = async (id) => { if (!window.confirm('Receive PO? Stock will be added and journal entries created.')) return; try { await purchaseOrderApi.receive(id); load(); loadStock(); } catch (err) { setError(err.response?.data?.detail || 'Failed'); } };

  const whName = (id) => warehouses.find(w => w.id === id)?.name || '-';
  const supName = (id) => suppliers.find(s => s.id === id)?.name || '-';
  const poSubtotal = poForm.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_cost) || 0), 0);

  const tabs = [
    { key: 'stock', label: 'Stock Levels', icon: Package },
    { key: 'movements', label: 'Movements', icon: ArrowDown },
    { key: 'purchase-orders', label: 'Purchase Orders', icon: ShoppingCart },
    { key: 'suppliers', label: 'Suppliers', icon: Truck },
    { key: 'warehouses', label: 'Warehouses', icon: Warehouse },
  ];

  return (
    <div className="space-y-4" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]" data-testid="inventory-heading">Inventory Management</h1>
          <p className="text-sm text-[#666]">Warehouses, stock, purchase orders & suppliers</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowMoveModal(true)} data-testid="add-movement-btn"><ArrowDown className="w-4 h-4 mr-1" /> Stock In/Out</Button>
          <Button size="sm" variant="secondary" onClick={() => setShowPOModal(true)} data-testid="create-po-btn"><ShoppingCart className="w-4 h-4 mr-1" /> New PO</Button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[#E0E0E0] overflow-x-auto" data-testid="inv-tabs">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key ? 'border-[#1A1F36] text-[#1A1F36]' : 'border-transparent text-[#888] hover:text-[#555]'}`}
            data-testid={`tab-${t.key}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}

      {/* Stock Levels */}
      {tab === 'stock' && (
        <div className="space-y-3">
          <div className="flex gap-3">
            <Select value={selWarehouse} onChange={e => setSelWarehouse(e.target.value)} className="max-w-xs">
              <option value="">All Warehouses</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </Select>
          </div>
          {stock.length === 0 ? <Card className="p-8 text-center text-[#666]">No stock data</Card> : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] text-[#999] uppercase border-b border-[#E0E0E0]">
                    <th className="px-4 py-2.5">Material</th><th className="px-4 py-2.5">Warehouse</th>
                    <th className="px-4 py-2.5 text-right">In</th><th className="px-4 py-2.5 text-right">Out</th>
                    <th className="px-4 py-2.5 text-right">Balance</th><th className="px-4 py-2.5 text-right">Value</th>
                  </tr></thead>
                  <tbody>
                    {stock.map((s, i) => (
                      <tr key={i} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`stock-row-${i}`}>
                        <td className="px-4 py-2.5 font-medium text-[#0A0A0A]">{s.material_name}</td>
                        <td className="px-4 py-2.5 text-[#666]">{s.warehouse_name}</td>
                        <td className="px-4 py-2.5 text-right text-[#00C853]">{s.total_in}</td>
                        <td className="px-4 py-2.5 text-right text-[#FF3333]">{s.total_out}</td>
                        <td className={`px-4 py-2.5 text-right font-bold ${s.balance > 0 ? 'text-[#002FA7]' : s.balance < 0 ? 'text-[#FF3333]' : ''}`}>{s.balance}</td>
                        <td className="px-4 py-2.5 text-right text-[#666]">{fmt(s.total_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Movements */}
      {tab === 'movements' && (
        <div className="space-y-3">
          {movements.length === 0 ? <Card className="p-8 text-center text-[#666]">No stock movements</Card> : (
            <Card className="divide-y divide-[#F0F0F0]">
              {movements.map(m => (
                <div key={m.id} className="flex items-center justify-between p-3 hover:bg-[#FAFAFA]" data-testid={`move-${m.id}`}>
                  <div className="flex items-center gap-3">
                    {m.movement_type === 'in' ? <ArrowDown className="w-4 h-4 text-[#00C853]" /> : <ArrowUp className="w-4 h-4 text-[#FF3333]" />}
                    <div>
                      <p className="font-medium text-sm text-[#0A0A0A]">{m.material_name}</p>
                      <div className="flex gap-2 text-xs text-[#666]">
                        <span>{whName(m.warehouse_id)}</span>
                        <span>{m.movement_type.toUpperCase()}</span>
                        <span>Qty: {m.quantity}</span>
                        {m.notes && <span className="text-[#999] italic">{m.notes}</span>}
                      </div>
                    </div>
                  </div>
                  <span className="font-bold text-sm text-[#0A0A0A]">{fmt(m.total_cost)}</span>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {/* Purchase Orders */}
      {tab === 'purchase-orders' && (
        <div className="space-y-3">
          {pos.length === 0 ? <Card className="p-8 text-center text-[#666]">No purchase orders</Card> : (
            <Card className="divide-y divide-[#F0F0F0]">
              {pos.map(po => (
                <div key={po.id} className="p-3 hover:bg-[#FAFAFA]" data-testid={`po-${po.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm text-[#0A0A0A]">{po.po_number}</p>
                      <div className="flex gap-2 text-xs text-[#666] mt-0.5">
                        <span>Supplier: {supName(po.supplier_id)}</span>
                        <span>WH: {whName(po.warehouse_id)}</span>
                        <span>{po.items?.length} items</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <p className="text-sm font-bold text-[#002FA7]">{fmt(po.total)}</p>
                        <Badge variant={po.status === 'received' ? 'success' : po.status === 'approved' ? 'primary' : 'default'}>{po.status}</Badge>
                      </div>
                      {po.status === 'draft' && <button onClick={() => handleApprovePO(po.id)} className="p-1.5 text-[#888] hover:text-[#002FA7] hover:bg-[#F0F4FF] rounded" title="Approve" data-testid={`approve-po-${po.id}`}><CheckCircle className="w-4 h-4" /></button>}
                      {(po.status === 'approved' || po.status === 'draft') && <button onClick={() => handleReceivePO(po.id)} className="p-1.5 text-[#888] hover:text-[#00C853] hover:bg-[#E8F5E9] rounded" title="Receive" data-testid={`receive-po-${po.id}`}><ArrowDown className="w-4 h-4" /></button>}
                      {po.status !== 'received' && <button onClick={async () => { if (window.confirm('Delete PO?')) { await purchaseOrderApi.delete(po.id); load(); } }} className="p-1.5 text-[#888] hover:text-[#FF3333] hover:bg-[#FEF2F2] rounded" data-testid={`del-po-${po.id}`}><Trash className="w-4 h-4" /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {/* Suppliers */}
      {tab === 'suppliers' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#666]">{suppliers.length} supplier(s)</span>
            <Button size="sm" onClick={() => setShowSupModal(true)} data-testid="add-supplier-btn"><Plus className="w-4 h-4 mr-1" /> Add Supplier</Button>
          </div>
          {suppliers.length === 0 ? <Card className="p-8 text-center text-[#666]">No suppliers</Card> : (
            <Card className="divide-y divide-[#F0F0F0]">
              {suppliers.map(s => (
                <div key={s.id} className="flex items-center justify-between p-3 hover:bg-[#FAFAFA]" data-testid={`sup-${s.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#FFAB00]/10 rounded-lg flex items-center justify-center"><Truck className="w-4 h-4 text-[#FFAB00]" /></div>
                    <div>
                      <p className="font-medium text-sm text-[#0A0A0A]">{s.name}</p>
                      <div className="flex gap-3 text-xs text-[#666]">
                        {s.contact_person && <span>{s.contact_person}</span>}
                        {s.email && <span>{s.email}</span>}
                        {s.trn && <span>TRN: {s.trn}</span>}
                      </div>
                    </div>
                  </div>
                  <button onClick={async () => { if (window.confirm('Delete?')) { await supplierApi.delete(s.id); load(); } }} className="p-1 text-[#666] hover:text-[#FF3333]" data-testid={`del-sup-${s.id}`}><Trash className="w-4 h-4" /></button>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {/* Warehouses */}
      {tab === 'warehouses' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#666]">{warehouses.length} warehouse(s)</span>
            <Button size="sm" onClick={() => setShowWhModal(true)} data-testid="add-wh-btn"><Plus className="w-4 h-4 mr-1" /> Add Warehouse</Button>
          </div>
          {warehouses.length === 0 ? <Card className="p-8 text-center text-[#666]">No warehouses</Card> : (
            <Card className="divide-y divide-[#F0F0F0]">
              {warehouses.map(w => (
                <div key={w.id} className="flex items-center justify-between p-3 hover:bg-[#FAFAFA]" data-testid={`wh-${w.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#002FA7]/10 rounded-lg flex items-center justify-center"><Warehouse className="w-4 h-4 text-[#002FA7]" /></div>
                    <div>
                      <p className="font-medium text-sm text-[#0A0A0A]">{w.name}</p>
                      <p className="text-xs text-[#666]">{w.location}</p>
                    </div>
                  </div>
                  <button onClick={async () => { if (window.confirm('Delete?')) { await inventoryApi.deleteWarehouse(w.id); load(); } }} className="p-1 text-[#666] hover:text-[#FF3333]" data-testid={`del-wh-${w.id}`}><Trash className="w-4 h-4" /></button>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {/* Warehouse Modal */}
      <Modal isOpen={showWhModal} onClose={() => setShowWhModal(false)} title="Add Warehouse">
        <form onSubmit={handleCreateWh} className="space-y-3" data-testid="wh-form">
          <Input label="Name" value={whForm.name} onChange={e => setWhForm({...whForm, name: e.target.value})} required data-testid="wh-name-input" />
          <Input label="Location" value={whForm.location} onChange={e => setWhForm({...whForm, location: e.target.value})} />
          <Select label="Project" value={whForm.project_id} onChange={e => setWhForm({...whForm, project_id: e.target.value})}>
            <option value="">-- None --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setShowWhModal(false)}>Cancel</Button>
            <Button type="submit" data-testid="wh-submit-btn">Create</Button>
          </div>
        </form>
      </Modal>

      {/* Supplier Modal */}
      <Modal isOpen={showSupModal} onClose={() => setShowSupModal(false)} title="Add Supplier">
        <form onSubmit={handleCreateSup} className="space-y-3" data-testid="sup-form">
          <Input label="Company Name" value={supForm.name} onChange={e => setSupForm({...supForm, name: e.target.value})} required data-testid="sup-name-input" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Contact Person" value={supForm.contact_person} onChange={e => setSupForm({...supForm, contact_person: e.target.value})} />
            <Input label="Email" value={supForm.email} onChange={e => setSupForm({...supForm, email: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Phone" value={supForm.phone} onChange={e => setSupForm({...supForm, phone: e.target.value})} />
            <Input label="TRN" value={supForm.trn} onChange={e => setSupForm({...supForm, trn: e.target.value})} />
          </div>
          <Input label="Address" value={supForm.address} onChange={e => setSupForm({...supForm, address: e.target.value})} />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setShowSupModal(false)}>Cancel</Button>
            <Button type="submit" data-testid="sup-submit-btn">Create</Button>
          </div>
        </form>
      </Modal>

      {/* Stock Movement Modal */}
      <Modal isOpen={showMoveModal} onClose={() => setShowMoveModal(false)} title="Stock In / Out">
        <form onSubmit={handleMove} className="space-y-3" data-testid="move-form">
          <Select label="Warehouse" value={moveForm.warehouse_id} onChange={e => setMoveForm({...moveForm, warehouse_id: e.target.value})} required>
            <option value="">-- Select --</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </Select>
          <Input label="Material Name" value={moveForm.material_name} onChange={e => setMoveForm({...moveForm, material_name: e.target.value})} required data-testid="move-material-input" />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Type" value={moveForm.movement_type} onChange={e => setMoveForm({...moveForm, movement_type: e.target.value})}>
              <option value="in">Stock In</option><option value="out">Stock Out</option>
            </Select>
            <Input label="Quantity" type="number" value={moveForm.quantity} onChange={e => setMoveForm({...moveForm, quantity: e.target.value})} required />
          </div>
          <Input label="Unit Cost" type="number" value={moveForm.unit_cost} onChange={e => setMoveForm({...moveForm, unit_cost: e.target.value})} />
          <Select label="Project (optional)" value={moveForm.project_id} onChange={e => setMoveForm({...moveForm, project_id: e.target.value})}>
            <option value="">-- None --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Input label="Notes" value={moveForm.notes} onChange={e => setMoveForm({...moveForm, notes: e.target.value})} />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setShowMoveModal(false)}>Cancel</Button>
            <Button type="submit" data-testid="move-submit-btn">Submit</Button>
          </div>
        </form>
      </Modal>

      {/* PO Modal */}
      <Modal isOpen={showPOModal} onClose={() => setShowPOModal(false)} title="Create Purchase Order">
        <form onSubmit={handleCreatePO} className="space-y-3" data-testid="po-form">
          <div className="grid grid-cols-2 gap-3">
            <Select label="Supplier" value={poForm.supplier_id} onChange={e => setPOForm({...poForm, supplier_id: e.target.value})} required>
              <option value="">-- Select --</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
            <Select label="Warehouse" value={poForm.warehouse_id} onChange={e => setPOForm({...poForm, warehouse_id: e.target.value})} required>
              <option value="">-- Select --</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </Select>
          </div>
          <Select label="Project" value={poForm.project_id} onChange={e => setPOForm({...poForm, project_id: e.target.value})}>
            <option value="">-- None --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <div className="space-y-2">
            <p className="text-sm font-medium" style={{ color: 'var(--text-heading)' }}>Items</p>
            {poForm.items.map((item, idx) => (
              <div key={idx} className="flex gap-2 items-end">
                <Input label={idx === 0 ? "Material" : ""} value={item.material_name} onChange={e => updatePOItem(idx, 'material_name', e.target.value)} required className="flex-1" />
                <Input label={idx === 0 ? "Qty" : ""} type="number" value={item.quantity} onChange={e => updatePOItem(idx, 'quantity', e.target.value)} required className="w-20" />
                <Input label={idx === 0 ? "Cost" : ""} type="number" value={item.unit_cost} onChange={e => updatePOItem(idx, 'unit_cost', e.target.value)} required className="w-24" />
                <Input label={idx === 0 ? "VAT%" : ""} type="number" value={item.tax_rate} onChange={e => updatePOItem(idx, 'tax_rate', e.target.value)} className="w-16" />
                {poForm.items.length > 1 && <button type="button" onClick={() => removePOItem(idx)} className="p-1 text-[#FF3333]"><Trash className="w-4 h-4" /></button>}
              </div>
            ))}
            <button type="button" onClick={addPOItem} className="text-xs text-[#002FA7] hover:underline">+ Add Item</button>
          </div>
          <div className="text-right text-sm font-semibold text-[#002FA7]">Subtotal: {fmt(poSubtotal)}</div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setShowPOModal(false)}>Cancel</Button>
            <Button type="submit" data-testid="po-submit-btn">Create PO</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
